import Card from '@/components/ui/Card';
import LoadingStatus from '@/components/ui/LoadingStatus';
import TrainingMetricsWidget from '@/components/dashboard/TrainingMetricsWidget';
import {
  DashboardHero,
  DashboardMetricStrip,
  DashboardSection,
  FixtureBrief,
  PriorityList,
  ReportDigest,
  type PriorityItem,
  type PrioritySeverity,
} from '@/components/dashboard/primitives';
import type { Alert, MedicalDashboardStats } from '@/types';

interface MedicalDashboardViewProps {
  data: MedicalDashboardStats;
  loading: boolean;
  error: string | null;
}

const severityOrder: Record<Alert['severity'], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const prioritySeverity: Record<Alert['severity'], PrioritySeverity> = {
  critical: 'critical',
  high: 'warning',
  medium: 'warning',
  low: 'info',
};

export default function MedicalDashboardView({ data, loading, error }: MedicalDashboardViewProps) {
  if (loading) {
    return <LoadingStatus variant="panel" label="Loading medical dashboard" className="h-64" />;
  }

  if (error) {
    return (
      <Card>
        <div className="text-red-600 text-sm p-4">{error}</div>
      </Card>
    );
  }

  const priorityItems: PriorityItem[] = [...data.criticalAlerts]
    .sort((left, right) => severityOrder[left.severity] - severityOrder[right.severity])
    .map((alert) => {
      const destination = alert.playerId
        ? { label: 'Open player record for', to: `/players/${alert.playerId}` }
        : alert.type === 'fixture'
          ? { label: 'Open matches for', to: '/matches' }
          : { label: 'Open reports for', to: '/reports' };

      return {
        id: alert.id,
        title: alert.message,
        context: `${alert.type} alert - ${alert.date}`,
        severity: prioritySeverity[alert.severity],
        action: destination,
      };
    });

  return (
    <div className="space-y-6">
      <DashboardHero
        title="Squad availability briefing"
        supportingText="Counts are dashboard markers, not case records or medical clearance. Return dates are not provided."
        accent="medical"
        primaryValue={data.injuredPlayers}
        primaryLabel="Players currently marked injured"
      />

      <DashboardSection
        title="Clinical priorities"
        description="Recent critical alerts"
      >
        <PriorityList
          ariaLabel="Recent critical alerts"
          items={priorityItems}
          emptyTitle="No recent critical alerts"
        />
      </DashboardSection>

      <DashboardMetricStrip
        ariaLabel="Medical squad metrics"
        metrics={[
          { label: 'Workspace players', value: data.totalPlayers },
          { label: 'Injured', value: data.injuredPlayers },
          { label: 'Recovery players', value: data.recoveryPlayers },
          { label: 'Average fitness', value: `${Math.round(data.fitnessAverage)}/100` },
        ]}
      />

      <DashboardSection
        title="Fitness and training context"
        description="Recorded training measures and immediate fixture context."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="min-w-0">
            <TrainingMetricsWidget metrics={data.trainingMetrics} />
          </div>
          <div className="min-w-0">
            <FixtureBrief fixtures={data.upcomingFixtures} emptyTitle="No upcoming fixtures scheduled" />
          </div>
        </div>
      </DashboardSection>

      <DashboardSection
        title="Recent reports"
        description="Latest contract-fed medical reporting for the workspace."
      >
        <ReportDigest
          reports={data.recentReports}
          createLabel="Create medical report"
          createTo="/reports/medical/new"
          emptyTitle="No recent medical reports"
          reportLinkLabel={(report) => `Open ${report.type} report for ${report.playerName}`}
        />
      </DashboardSection>
    </div>
  );
}
